import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import chunk from 'lodash/chunk';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Row } from '../../../../../CommonComponents/OPL/Grid/Row';
import { Column } from '../../../../../CommonComponents/OPL/Grid/Column';
import { MarginProps } from '../../../../../theme/styledProps';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { Toggle } from '../../../../../CommonComponents/OPL/Toggle/Toggle';
import { SettingsGrid } from '../SettingsGrid/SettingsGrid';
import { AssumptionsDTO } from '../../api/useFirmSettingsQuery';
import { AssumptionField } from './components/AssumptionField/AssumptionField';
import { AssumptionDefinition, ASSUMPTIONS_DEFINITIONS } from './Assumptions.meta';
import { showFinancialPlanningSelector } from '../../../../../AdviserArea/AdviserAreaSelector';
import { CMAType } from '../../../../../AdviserSettings/Page/CustomModelAssumptions/types';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { PlanStartYearDTO } from '../../../../../ClientSettings/api/useClientSettingsQuery';
import { Tooltip } from '../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { RetirementExpenses } from '../../../../../ProfileOPL/Goals/goals/Retirement/Retirement.meta';

interface Props extends MarginProps {
  isReadonly?: boolean;
  isFirm?: boolean;
  firmAssumptions: AssumptionsDTO;
  formBag: UseFormReturn<any>;
  is3dRiskToleranceWorkflowEnabled?: boolean;
  isCustomAssumptionsEnabled?: boolean;
  onOpenCurrentYearAssumptionsModal(): void;
}

export const Assumptions = ({
  isReadonly,
  isFirm,
  formBag,
  firmAssumptions,
  is3dRiskToleranceWorkflowEnabled,
  isCustomAssumptionsEnabled,
  onOpenCurrentYearAssumptionsModal,
  ...rest
}: Props) => {
  const showFinancialPlanning = useSelector(showFinancialPlanningSelector);

  const fieldName = isFirm ? 'firmAssumptions' : 'adviserAssumptions';

  const { watch } = formBag;

  const planStartYear = watch(`${fieldName}.planStartYear`);

  const selectedRetirementExpense = watch(`${fieldName}.retirementExpenses`);

  if (!showFinancialPlanning) {
    return null;
  }

  const assumptionsDefinitions: AssumptionDefinition[] = ASSUMPTIONS_DEFINITIONS.map(assumption => {
    if (assumption.subFieldName === 'riskToleranceWorkflow' && !is3dRiskToleranceWorkflowEnabled) {
      return null;
    }

    if (assumption.subFieldName === 'capitalMarketAssumptions' && isFirm) {
      return null;
    }

    if (assumption.subFieldName === 'capitalMarketAssumptions' && assumption.options) {
      return {
        ...assumption,
        options: isCustomAssumptionsEnabled
          ? assumption.options
          : assumption.options.filter(option => option.value !== CMAType.CustomCMAs),
      };
    }

    if (assumption.subFieldName === 'retirementExpenses' && assumption.options) {
      return {
        ...assumption,
        options: watch(`${fieldName}.retirementExpensesOptions`) ?? [],
      };
    }

    return assumption;
  }).filter(assumption => assumption !== null) as AssumptionDefinition[];

  return (
    <Box as="section" {...rest}>
      <SectionHeader title="Plan Assumptions" />

      <SettingsGrid>
        {chunk(assumptionsDefinitions, 3).map(assumptionsRow => (
          <Row key={assumptionsRow[0].subFieldName} mb="32">
            {assumptionsRow.map(assumption => {
              if (
                assumption.subFieldName === 'retirementExpensesAmount' &&
                selectedRetirementExpense !== RetirementExpenses.Other
              ) {
                return null;
              }

              return (
                <Column key={assumption.subFieldName} width={4}>
                  <AssumptionField
                    {...assumption}
                    fieldName={fieldName}
                    firmAssumptions={firmAssumptions}
                    formBag={formBag}
                    isFirm={isFirm}
                    isReadonly={isReadonly}
                  />
                  {assumption.subFieldName === 'planStartYear' &&
                    planStartYear === PlanStartYearDTO.CurrentYear && (
                      <Button
                        mt="8"
                        type="button"
                        variant="link"
                        leftIcon="far fa-pencil"
                        onClick={onOpenCurrentYearAssumptionsModal}
                      >
                        Manage Current Year Assumptions
                      </Button>
                    )}
                </Column>
              );
            })}
          </Row>
        ))}
      </SettingsGrid>

      <Box display="flex" alignItems="center" mt="24">
        <Toggle
          {...formBag.register(`${fieldName}.enhancedReportExperience`)}
          disabled={isReadonly}
        >
          Enable Enhanced Report Generation Experience
        </Toggle>

        <Tooltip content="The enhanced experience allows you to view and save a draft report before finalization. Final reports are archived and timestamped for future access and review.">
          <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" ml="4" />
        </Tooltip>
      </Box>

      <Toggle
        {...formBag.register(`${fieldName}.enableAutoGeneratedWhatIfs`)}
        disabled={isReadonly}
        mt="24"
      >
        Enable Auto-Generated What-ifs
      </Toggle>

      {isFirm && (
        <Toggle {...formBag.register('assumptionsEditableByAdviser')} mt="24">
          Allow Advisors to Override Firm Assumptions
        </Toggle>
      )}
    </Box>
  );
};
